import { useState, useMemo } from 'react';
import { X, CheckCircle2, XCircle, Calendar } from 'lucide-react';
import { useRoosterStore } from '../store/useRoosterStore.js';
import { checkVerlof } from '../engine/verlof-check.js';
import { schoolweekType } from '../engine/rooster-engine.js';
import { bouwAfwezigheidMap, berekenUniekeDagCodes, DAG_KORT, ALLE_DAGEN, formatDatum } from '../data/afwezigheid.js';
import { TYPEN_MET_UREN } from '../data/verlofuren.js';
import { weekverdelingTekst, weekVoorstel, weekTeller, toonUren } from '../lib/verlofLabels.js';
import { AFWEZIG_TYPE } from '../lib/roostercodes.js';
import { isoWeekNummer, weekNaarMaandag } from '../data/maand.js';
import { vakantieDagenInWeek } from '../data/vakanties.js';
import { genereerMaandViaBlokken } from '../engine/blok-generator.js';
import { isRoosterbaarVolgensRollen } from '../lib/rollen.js';
import { vandaagNL, dagPlus } from '../lib/tijd.js';

// Vandaag als default startdatum (JJJJ-MM-DD). Via vandaagNL, niet toISOString:
// die rekent naar UTC en zou rond middernacht op gisteren openen — zie lib/tijd.js.
function vandaagStr() {
  return vandaagNL();
}

// Einde: een week later als default. Doorrekenen vanaf de NEDERLANDSE dag, zodat
// begin- en einddatum niet uit elkaar kunnen lopen rond de dagovergang.
function overEenWeekStr() {
  return dagPlus(vandaagNL(), 6);
}

function aantalDagen(van, tot) {
  if (!van || !tot) return 0;
  const ms = new Date(tot + 'T00:00:00') - new Date(van + 'T00:00:00');
  return Math.max(0, Math.floor(ms / 86400000) + 1);
}

/**
 * De ingevoerde uren als getal, of null zodra de invoer niet deugt.
 *
 * GELDIG = groter dan 0, hoogstens twee decimalen. Numbers levert gebroken getallen —
 * Leroy (28u) kost 5,60u per losse werkdag, do+vr samen 11,20u — dus de KOMMA moet kunnen;
 * die wordt hier op een punt gezet voordat Number() eraan komt. Een leeg veld, een min-teken,
 * drie decimalen of losse tekst geven null, en dan blijft opslaan geblokkeerd.
 *
 * `nulMag` is de schakelaar voor de weekvelden: daar is 0 een geldig bedrag, een week die niets
 * kost (bijvoorbeeld alleen een weekend). Het totaalveld laat 0 niet toe. Verder lezen beide
 * velden met precies dezelfde regels: één functie, geen tweede kopie.
 *
 * Bewust STRENGER dan de leeslaag: ingevuldeUrenVanRecord (data/verlofuren.js) accepteert
 * elk eindig getal, precies zoals overrideUren dat voor de kantooruren doet. Het formulier
 * is de poort voor het bereik, niet de leeslaag — zelfde rolverdeling als bij de kantooruren.
 */
function parseUren(tekst, { nulMag = false } = {}) {
  const s = String(tekst ?? '').trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) && (nulMag ? n >= 0 : n > 0) ? n : null;
}

/** 'JJJJ-MM-DD' → "26 mei 2026". Voor de overlap-melding hieronder. */
const datumLabel = (s) => formatDatum(new Date(s + 'T00:00:00'), true);

/** Twee datumbereiken (allebei inclusief) die elkaar raken? Tekstvergelijking mag: ISO sorteert. */
const periodesOverlappen = (vanA, totA, vanB, totB) => vanA <= totB && vanB <= totA;

export default function VerlofModal({ onSluit }) {
  const { regels, afwezigheid, vastgelegdeWeken, vakanties, addAfwezigheid } = useRoosterStore();

  const medewerkers = Object.entries(regels.medewerkers)
    .filter(([k, mw]) => !k.startsWith('_') && isRoosterbaarVolgensRollen(mw?.rollen))
    .map(([key, mw]) => ({ key, naam: mw.naam }));

  const gemeenteOpties = Object.entries(regels.gemeenten)
    .filter(([k, g]) => !k.startsWith('_') && g.actief !== false)
    .map(([k, g]) => ({ key: k, naam: g.naam }));

  const [naam, setNaam]             = useState('');
  const [type, setType]             = useState('verlof');
  const [vanDatum, setVanDatum]     = useState(vandaagStr());
  const [totDatum, setTotDatum]     = useState(overEenWeekStr());
  const [gemeenteKey, setGemeenteKey] = useState(gemeenteOpties[0]?.key || 'arnhem');
  const [omschrijving, setOmschrijving] = useState('');
  const [advies, setAdvies]         = useState(null);
  // Uren: LEEG bij openen, bewust zonder voorstel. Het getal komt uit Numbers en
  // moet overgenomen worden; een voorgevulde berekening zou weggeklikt kunnen worden en
  // daarmee stilzwijgend een verzonnen getal opleveren.
  const [urenTekst, setUrenTekst]   = useState('');
  // Wat de gebruiker in de weekvelden getypt heeft: de tekst per week, plus het totaal waar die
  // teksten bij horen. null = er is niets getypt, dan tonen de velden het voorstel. Er is geen vlag
  // per veld; of er iets afwijkt, zeggen de bedragen zelf (zie slaOp).
  // DE REGEL: het handwerk hoort bij één medewerker, één periode en één totaal. Wordt een daarvan
  // een andere geldige waarde, dan is het weg en staat het voorstel er weer.
  const [weekHandwerk, setWeekHandwerk] = useState(null);

  // Bereken welke unieke dagcodes getroffen worden
  const getroffen = vanDatum && totDatum ? berekenUniekeDagCodes(vanDatum, totDatum) : [];
  const datumOk   = vanDatum && totDatum && totDatum >= vanDatum;

  // ── Uren uit Numbers — alleen bij verlof en ziekte (TYPEN_MET_UREN), en daar VERPLICHT ──
  // Een typewissel laat urenTekst staan, en ook het handwerk in de weekvelden: verlof of ziekte
  // verandert de verdeling niet. Bij 'anders' is het veld verborgen, telt het niet mee voor
  // opslaan en komt het niet op het record; terug naar verlof of ziekte en het getal staat er
  // weer, zichtbaar, en wordt opnieuw gecontroleerd.
  const vraagtUren = TYPEN_MET_UREN.includes(type);
  const uren       = parseUren(urenTekst);
  const urenOk     = !vraagtUren || uren !== null;
  const urenFout   = vraagtUren && uren === null;   // stuurt de rode rand aan

  // ── Wat de app per week boekt ─────────────────────────────────────────────
  // Het voorstel komt uit lib/verlofLabels.js (weekVoorstel, op verdeelIngevuldeUren): exact de
  // verdeling die engine en balans lezen, geen tweede rekensom in de UI. Alleen bij een geldig
  // getal, want zonder getal valt er niets te verdelen. Bij één gedekte week valt er ook niets te
  // verdelen; dan staat er de grijze regel ("wk44 8u"). Bij meer weken staan er weekvelden, gevuld
  // met dit voorstel.
  const voorstel = useMemo(
    () => (naam && datumOk && uren !== null
      ? weekVoorstel({ naam, type, vanDatum, totDatum, uren }, regels)
      : null),
    [naam, type, vanDatum, totDatum, datumOk, uren, regels],
  );
  const metWeekvelden = !!voorstel && voorstel.length > 1;
  const weekverdeling = voorstel && voorstel.length === 1
    ? weekverdelingTekst({ naam, type, vanDatum, totDatum, uren }, regels)
    : null;

  // De weekvelden tonen het handwerk als dat er is, anders het voorstel. Typen verandert alleen
  // het veld waarin getypt wordt; er rekent niets om. Een leeg of ongeldig veld (bedrag null)
  // krijgt een rode rand, telt in de teller als 0 en houdt opslaan dicht.
  const weekTeksten = metWeekvelden
    ? Object.fromEntries(voorstel.map((w) => [w.sleutel, weekHandwerk ? weekHandwerk.teksten[w.sleutel] : toonUren(w.uren)]))
    : null;
  const weekBedragen = metWeekvelden
    ? voorstel.map((w) => parseUren(weekTeksten[w.sleutel], { nulMag: true }))
    : null;
  const weekOngeldig = metWeekvelden && weekBedragen.some((b) => b === null);
  // Groen of rood zegt de leeslaag zelf (weekTeller): neemt verdeelIngevuldeUren deze bedragen
  // letterlijk over, dan klopt het. Zo staat het formulier nooit groen op iets dat hij weigert.
  const teller = metWeekvelden
    ? weekTeller(
      { naam, type, vanDatum, totDatum, uren },
      regels,
      Object.fromEntries(voorstel.map((w, i) => [w.sleutel, weekBedragen[i] ?? 0])),
    )
    : null;
  const weekOk = !metWeekvelden || (teller.klopt && !weekOngeldig);

  // ── Overlap met een bestaand record van dezelfde medewerker ────────────────
  // Twee records die elkaar op één dag raken verstoren elkaar stil: bouwAfwezigheidTypeMap
  // laat het LAATSTE record het type van die dag bepalen, en de credit-lus telt de ingevulde
  // uren van zo'n dag alleen mee als ze van een record van dát type komen. Dat is niet te zien
  // op het scherm, dus wordt het hier geblokkeerd in plaats van achteraf verklaard.
  // Records in het oude formaat ({ dagen: [...] }, zonder datums) kunnen niet op periode
  // vergeleken worden en blijven buiten deze check.
  const overlap = useMemo(() => {
    if (!naam || !datumOk) return null;
    return (afwezigheid || []).find(
      (a) => a.naam === naam && a.vanDatum && a.totDatum
        && periodesOverlappen(vanDatum, totDatum, a.vanDatum, a.totDatum),
    ) || null;
  }, [afwezigheid, naam, vanDatum, totDatum, datumOk]);

  const kanOpslaan = naam && datumOk && urenOk && weekOk && !overlap;

  function berekenAdvies() {
    if (!naam || !datumOk || getroffen.length === 0) return;
    const mwKey = medewerkers.find(m => m.naam === naam)?.key;
    if (!mwKey) return;

    // Per CONCRETE datum in het bereik de ISO-week + ISO-jaar van DIE dag bepalen en daartegen
    // checken — niet tegen het globale store-weeknummer. Zo valt een verlof over een weekgrens
    // per dag in de juiste week (26 jun -> 2026-26, 1 jul -> 2026-27) en matcht de
    // vastgelegdeWeken-sleutel `${isoJaar}-${isoWeek}` waaronder legWeekVast opslaat.
    const afwMapPerWeek = {}; // memo: `${isoJaar}-${isoWeek}` -> week-specifieke afwezigheidMap
    // Vastgelegde-set in het BLOK-pad-formaat (`${isoJaar}:${weeknummer}` met DUBBELE PUNT,
    // value = het engine-weekobject) — zelfde omzetting als genereerMaand in de store. Verschilt
    // bewust van het store-formaat (streepje) dat de vastgelegd-lookup hieronder gebruikt.
    const vastgelegdeSet = {};
    for (const v of Object.values(vastgelegdeWeken)) vastgelegdeSet[`${v.isoJaar}:${v.weeknummer}`] = v.week;
    // Memo voor de wat-als per NIET-vastgelegde week: weekKey -> { A: weergaveWeek, B: weergaveWeek }
    // (A = zonder, B = met hypothetisch verlof). Op weekniveau, dus meerdere dagen in dezelfde
    // week genereren niet opnieuw.
    const watAlsPerWeek = {};
    const resultaten = [];
    const tot = new Date(totDatum + 'T00:00:00');
    const cursor = new Date(vanDatum + 'T00:00:00');
    while (cursor <= tot) {
      const dag = ALLE_DAGEN[(cursor.getDay() + 6) % 7]; // getDay() (zo=0) -> dagcode
      const { week: isoWeek, isoJaar } = isoWeekNummer(cursor);
      const weekKey = `${isoJaar}-${isoWeek}`;
      // Afwezigheid-Map is week-specifiek (weekend-rotatie + datum->dagcode per week), dus
      // per week bouwen i.p.v. één globale map.
      if (!afwMapPerWeek[weekKey]) afwMapPerWeek[weekKey] = bouwAfwezigheidMap(afwezigheid, isoWeek, isoJaar);
      const roosterVastgelegd = vastgelegdeWeken[weekKey]?.week?.rooster || null;

      if (roosterVastgelegd) {
        // STAP 1 (ONGEWIJZIGD): vergrendelde week -> tel het werkelijk ingeroosterde rooster.
        // Vakantiedagen van DEZE week meegeven (zelfde bouw als blok-generator.js:44): de engine
        // onderdrukt de dagdienst op een vakantiedag, en zonder die set leest checkVerlof de
        // bewust-lege cel als onderbezetting.
        const vakDagen = new Set(vakantieDagenInWeek(isoWeek, isoJaar, vakanties));
        const res = checkVerlof(regels, afwMapPerWeek[weekKey], mwKey, gemeenteKey, dag, isoWeek, schoolweekType(isoWeek), roosterVastgelegd, vakDagen);
        // ernst (alleen bij !ok): rood als een onderbezette dienst <=1 beschikbare persoon
        // overhoudt (geen werkbaar koppel), anders oranje. checkVerlof rekent in PERSONEN.
        const ernst = res.ok ? null : ((res.diensten || []).some((d) => !d.ok && d.beschikbaar <= 1) ? 'rood' : 'oranje');
        resultaten.push({ dag, ...res, ernst, weekKey, isoWeek, isoJaar });
      } else {
        // STAP 2: niet-vastgelegde week -> engine wat-als via EXACT hetzelfde blok-pad als de live
        // maand-weergave. Twee runs (A zonder, B met hypothetisch verlof), per week gememoïseerd.
        if (!watAlsPerWeek[weekKey]) {
          const ma = weekNaarMaandag(isoWeek, isoJaar); // Regel B: maand uit de week zelf afleiden
          const jaar = ma.getFullYear();
          const maand = ma.getMonth() + 1;
          const hyp = [...afwezigheid, { naam, type, vanDatum, totDatum }]; // store blijft ongemoeid
          const vind = (res) => res.weergave.find(w => w.weeknummer === isoWeek && w.isoJaar === isoJaar);
          watAlsPerWeek[weekKey] = {
            A: vind(genereerMaandViaBlokken(regels, afwezigheid, jaar, maand, vastgelegdeSet, vakanties)),
            B: vind(genereerMaandViaBlokken(regels, hyp, jaar, maand, vastgelegdeSet, vakanties)),
          };
        }
        const { A, B } = watAlsPerWeek[weekKey];
        const sleutel = (c) => `${c.gemeenteKey}|${c.dienst}|${c.dag}`;
        // A-conflicten per slot, zodat we B's personen-aantal tegen A kunnen leggen.
        const aPerSlot = new Map((A?.conflicten || []).map((c) => [sleutel(c), c]));
        // Toon op DEZE dag+gemeente elk B-conflict dat nieuw is (niet in A) OF minder personen telt dan in A.
        const nieuweGaten = (B?.conflicten || []).filter((c) => {
          if (c.gemeenteKey !== gemeenteKey || c.dag !== dag) return false;
          const a = aPerSlot.get(sleutel(c));
          return !a || (c.ingepland?.length ?? 0) < (a.ingepland?.length ?? 0);
        });
        const gemeente = regels.gemeenten[gemeenteKey];
        const ok = nieuweGaten.length === 0;
        // ernst (alleen bij !ok): de engine-conflicten dragen 'ernst' al (STAP 2). Rood zodra
        // één gat rood is (<=1 ingeplande persoon), anders oranje; ontbrekende ernst -> rood.
        const ernst = ok ? null : (nieuweGaten.every((c) => c.ernst === 'oranje') ? 'oranje' : 'rood');
        resultaten.push({
          dag,
          ok,
          ernst,
          weekKey,
          isoWeek,
          isoJaar,
          uitleg: ok
            ? `Dit verlof veroorzaakt geen extra bezettingsprobleem voor ${gemeente.naam} op deze dag.`
            : [...nieuweGaten]
                .sort((x, y) => (x.ernst === 'oranje' ? 1 : 0) - (y.ernst === 'oranje' ? 1 : 0))
                .map((c) => {
                  const X = c.ingepland?.length ?? 0;
                  const dienstLabel = gemeente.diensten[c.dienst]?.label || c.dienst;
                  return c.ernst === 'oranje'
                    ? `Na dit verlof blijven er ${X} personen op ${dienstLabel} in ${gemeente.naam} — krap maar werkbaar.`
                    : `Na dit verlof blijft er maar ${X} ${X === 1 ? 'persoon' : 'personen'} op ${dienstLabel} in ${gemeente.naam} — te weinig.`;
                })
                .join(' '),
          diensten: nieuweGaten.map((c) => ({
            dienstId: c.dienst,
            label: gemeente.diensten[c.dienst]?.label || c.dienst,
            ernst: c.ernst,
            ok: false,
          })),
        });
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    setAdvies(resultaten);
  }

  // Het totaal verandert. Alleen een ANDER geldig totaal maakt het handwerk in de weekvelden
  // waardeloos, want dat hoorde bij een andere verdeling. Een tussenstand die nog geen getal is
  // ("48," op weg naar "48,5") doet niets.
  function wijzigTotaal(tekst) {
    setUrenTekst(tekst);
    const nieuw = parseUren(tekst);
    if (nieuw !== null && nieuw !== weekHandwerk?.totaal) setWeekHandwerk(null);
  }

  // Typen in een weekveld verandert alleen dat ene veld. De andere velden houden wat er staat,
  // ook als dat nog het voorstel is; vanaf nu horen ze allemaal bij het handwerk voor dit totaal.
  function wijzigWeek(sleutel, tekst) {
    setWeekHandwerk({ totaal: uren, teksten: { ...weekTeksten, [sleutel]: tekst } });
  }

  function slaOp() {
    if (!kanOpslaan) return;
    // `uren` komt ALLEEN op een verlof- of ziekterecord te staan (TYPEN_MET_UREN). Bij 'anders'
    // blijft het veld helemaal weg — ook als er van een eerder gekozen type nog tekst in het
    // verborgen urenveld staat — en dan geldt onverkort het oude pad: 8u per would-be-werkdag
    // met de bestaande aftopping (lib/balans.js, engine/rooster-engine.js).
    //
    // `urenPerWeek` gaat ALLEEN mee als minstens één weekbedrag afwijkt van het voorstel. Wijkt
    // niets af, dan houdt het record de vorm die het nu heeft en volgt de leeslaag het
    // naar-rato-pad. Dat levert precies dit voorstel op, want het voorstel IS dat pad. Zo draagt
    // een record alleen weekbedragen als iemand er bewust iets aan veranderd heeft.
    // De vergelijking gaat over de bedragen zelf, niet over een vlag: wie een veld wijzigt en
    // daarna terugzet, heeft niets veranderd. En ze gaat in centen, de precisie van het formulier.
    // De restweek van het voorstel kan in de laatste bit afwijken (38,40 − 25 = 13,399999999999999),
    // terwijl het veld 13,4 toont en 13,4 teruggeeft.
    const afwijkend = metWeekvelden
      && voorstel.some((w, i) => Math.round(weekBedragen[i] * 100) !== Math.round(w.uren * 100));
    const urenPerWeek = afwijkend
      ? Object.fromEntries(voorstel.map((w, i) => [w.sleutel, weekBedragen[i]]))
      : null;
    addAfwezigheid(vraagtUren
      ? { naam, type, vanDatum, totDatum, omschrijving, uren, ...(urenPerWeek ? { urenPerWeek } : {}) }
      : { naam, type, vanDatum, totDatum, omschrijving });
    onSluit();
  }

  const allOk = advies && advies.every(a => a.ok);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">Verlof / Ziekte invoeren</h2>
          <button onClick={onSluit} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Medewerker */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Medewerker</label>
            <select
              value={naam}
              onChange={e => { setNaam(e.target.value); setAdvies(null); setWeekHandwerk(null); }}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="">Kies medewerker…</option>
              {medewerkers.map(m => <option key={m.key} value={m.naam}>{m.naam}</option>)}
            </select>
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
            <div className="flex gap-2">
              {['verlof', 'ziekte', 'anders'].map(t => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition ${
                    type === t ? 'bg-brand-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Datumbereik */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Periode</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-slate-500 mb-1">Van</div>
                <input
                  type="date"
                  value={vanDatum}
                  onChange={e => { setVanDatum(e.target.value); setAdvies(null); setWeekHandwerk(null); }}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Tot en met</div>
                <input
                  type="date"
                  value={totDatum}
                  min={vanDatum}
                  onChange={e => { setTotDatum(e.target.value); setAdvies(null); setWeekHandwerk(null); }}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>

            {/* Periode-samenvatting */}
            {datumOk && (
              <div className="mt-2 flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 text-sm text-slate-600">
                <Calendar className="w-4 h-4 shrink-0 text-slate-400" />
                {/* KALENDERdagen en hun dagcodes — niet "werkdagen". Dat woord is hieronder
                    gereserveerd voor de would-be-werkdagen van DEZE medewerker, en dat is een
                    ander (lager) getal: een zaterdag valt hier wel in het bereik, maar telt
                    voor iemand met een weekend-OFF niet als werkdag. Twee keer hetzelfde woord
                    met twee betekenissen onder elkaar las verkeerd. */}
                <span>
                  <strong>{aantalDagen(vanDatum, totDatum)}</strong> dag{aantalDagen(vanDatum, totDatum) !== 1 ? 'en' : ''}
                  {getroffen.length > 0 && (
                    <span className="text-slate-400 ml-1">
                      · {getroffen.map(d => DAG_KORT[d]).join(', ')}
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Overlap met een bestaand record — blokkeert opslaan */}
          {overlap && (
            <div className="flex items-start gap-2 rounded-lg px-4 py-3 text-sm bg-red-50 text-red-800">
              <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                <strong className="mr-1">Overlapt met bestaand record.</strong>
                {naam} staat al geregistreerd als{' '}
                <strong>{(AFWEZIG_TYPE[overlap.type] || AFWEZIG_TYPE.anders).label}</strong>{' '}
                {overlap.vanDatum === overlap.totDatum
                  ? `op ${datumLabel(overlap.vanDatum)}`
                  : `van ${datumLabel(overlap.vanDatum)} t/m ${datumLabel(overlap.totDatum)}`}
                {overlap.omschrijving && ` (${overlap.omschrijving})`}.
                {' '}Verwijder dat record eerst, of kies een periode die er niet overheen valt.
              </span>
            </div>
          )}

          {/* Uren uit Numbers — alleen bij verlof en ziekte, en daar verplicht */}
          {vraagtUren && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Uren uit Numbers <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={urenTekst}
                onChange={e => wijzigTotaal(e.target.value)}
                placeholder="bijv. 6,4"
                aria-invalid={urenFout}
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                  urenFout
                    ? 'border-red-400 bg-red-50 text-red-900 placeholder-red-300 focus:ring-red-400'
                    : 'border-slate-200 focus:ring-brand-500'
                }`}
              />
              {/* Onder het veld óf de melding (leeg of ongeldig), óf wat de app per week boekt —
                  nooit allebei: een verdeling bestaat alleen bij een geldig getal. Bij één gedekte
                  week de grijze regel, bij meer weken een veld per week met de teller eronder. Elk
                  op een eigen regel over de volle breedte, zodat een lange periode gewoon doorloopt. */}
              {urenFout && (
                <div className="mt-1.5 text-xs text-red-600">
                  {urenTekst.trim() === ''
                    ? 'Verplicht — neem het totaal voor de hele periode over uit Numbers.'
                    : 'Ongeldig — een getal groter dan 0, met hoogstens twee decimalen.'}
                </div>
              )}
              {weekverdeling && (
                <div className="mt-1.5 text-xs text-slate-400">{weekverdeling}</div>
              )}
              {metWeekvelden && (
                <div className="mt-2">
                  <div className="flex flex-wrap gap-x-3 gap-y-2">
                    {voorstel.map((w, i) => (
                      <label key={w.sleutel} className="flex items-center gap-1.5 text-xs text-slate-500">
                        {w.week}
                        <input
                          type="text"
                          inputMode="decimal"
                          value={weekTeksten[w.sleutel] ?? ''}
                          onChange={e => wijzigWeek(w.sleutel, e.target.value)}
                          aria-invalid={weekBedragen[i] === null}
                          className={`w-16 border rounded-lg px-2 py-1 text-sm text-right tabular-nums focus:outline-none focus:ring-2 ${
                            weekBedragen[i] === null
                              ? 'border-red-400 bg-red-50 text-red-900 focus:ring-red-400'
                              : 'border-slate-200 focus:ring-brand-500'
                          }`}
                        />
                      </label>
                    ))}
                  </div>
                  <div className={`mt-1.5 text-xs ${teller.klopt ? 'text-emerald-600' : 'text-red-600'}`}>
                    {teller.tekst}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Gemeente voor check */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Gemeente (voor bezettingscheck)
            </label>
            <div className="flex gap-2 flex-wrap">
              {gemeenteOpties.map(g => (
                <button
                  key={g.key}
                  onClick={() => { setGemeenteKey(g.key); setAdvies(null); }}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                    gemeenteKey === g.key
                      ? 'bg-brand-700 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {g.naam}
                </button>
              ))}
            </div>
          </div>

          {/* Omschrijving */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Omschrijving (optioneel)</label>
            <input
              type="text"
              value={omschrijving}
              onChange={e => setOmschrijving(e.target.value)}
              placeholder="bijv. vakantie Turkije"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          {/* Verlof-check */}
          <button
            onClick={berekenAdvies}
            disabled={!naam || !datumOk || getroffen.length === 0}
            className="w-full py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-40 transition"
          >
            Controleer bezetting per dag
          </button>

          {advies && (() => {
            // Groepeer op weekKey in eerste-voorkomen-volgorde (Map behoudt insert-volgorde).
            const perWeek = new Map();
            for (const item of advies) {
              if (!perWeek.has(item.weekKey)) perWeek.set(item.weekKey, []);
              perWeek.get(item.weekKey).push(item);
            }
            return (
              <div className="space-y-4">
                {[...perWeek.entries()].map(([weekKey, items]) => {
                  const isoWeek = items[0].isoWeek;
                  const problemen = items.filter((a) => !a.ok);
                  const okDagen = items.filter((a) => a.ok);
                  // rood vóór oranje; binnen hetzelfde niveau de bestaande dag-volgorde (stabiele sort).
                  const problemenGesorteerd = [...problemen].sort((x, y) => (x.ernst === 'oranje' ? 1 : 0) - (y.ernst === 'oranje' ? 1 : 0));
                  return (
                    <div key={weekKey} className="space-y-2">
                      <div className="text-xs font-semibold text-slate-500 uppercase mt-1">Week {isoWeek}</div>
                      {problemenGesorteerd.map((a, i) => {
                        // ok -> groen; niet-ok -> amber bij ernst 'oranje', anders rood (veiligste fallback).
                        const vak = a.ok
                          ? 'bg-green-50 text-green-800'
                          : a.ernst === 'oranje'
                            ? 'bg-amber-50 text-amber-800'
                            : 'bg-red-50 text-red-800';
                        return (
                          <div
                            key={i}
                            className={`flex items-start gap-2 rounded-lg px-4 py-3 text-sm ${vak}`}
                          >
                            {a.ok
                              ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                              : <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
                            }
                            <span>
                              <strong className="mr-1">{DAG_KORT[a.dag]}</strong>
                              {a.uitleg}
                            </span>
                          </div>
                        );
                      })}

                      {okDagen.length > 0 && (
                        <div className="flex items-start gap-2 rounded-lg px-4 py-3 text-sm bg-green-50 text-green-800">
                          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                          <span>
                            {problemen.length === 0
                              ? 'Geen bezettingsproblemen deze week.'
                              : `Geen bezettingsprobleem op de overige dagen (${okDagen.map((a) => DAG_KORT[a.dag]).join(', ')}).`}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-slate-100">
          <button
            onClick={onSluit}
            className="flex-1 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition"
          >
            Annuleren
          </button>
          <button
            onClick={slaOp}
            disabled={!kanOpslaan}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold text-white transition disabled:opacity-40 ${
              advies && !allOk ? 'bg-orange-500 hover:bg-orange-600' : 'bg-brand-700 hover:bg-brand-800'
            }`}
          >
            {advies && !allOk ? 'Toch opslaan (conflict!)' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}
